import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Chip,
  Checkbox,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  CircularProgress,
  Box,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ClearIcon from '@material-ui/icons/Clear';
import { KeyboardDatePicker } from '@material-ui/pickers';
import VisibilityIcon from '@material-ui/icons/Visibility';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useHistory, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';

import PageTitle from '../../components/PageTitle';
import Widget from '../../components/Widget/Widget';
import TableData from '../../components/TableData';
import FaturaDetalhesDialog from './FaturaDetalhesDialog';
import { useLayoutDispatch, toggleLoading } from '../../context/LayoutContext';
import useStyles from './styles';
import api from '../../services/api';
import { getError } from '../../utils/getResponseError';
import {
  basicDate,
  parseISO,
  dateToBD,
} from '../../utils/dateFormatter';
import { toCurrency } from '../../utils/mask';

export default function Faturas() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const layoutDispatch = useLayoutDispatch();

  const [faturas, setFaturas] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const [estados, setEstados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [selectedClienteNome, setSelectedClienteNome] = useState('');

  const [vencimentoInicio, setVencimentoInicio] = useState(null);
  const [vencimentoFim, setVencimentoFim] = useState(null);
  const [emissaoInicio, setEmissaoInicio] = useState(null);
  const [emissaoFim, setEmissaoFim] = useState(null);
  const [situacoesSelecionadas, setSituacoesSelecionadas] = useState([]);
  const [clienteId, setClienteId] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogFaturaId, setDialogFaturaId] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [situacaoMenuAnchor, setSituacaoMenuAnchor] = useState(null);

  useEffect(() => {
    carregarEstados();
    carregarClientes();
  }, []);

  // Sincroniza o nome exibido no Autocomplete quando clienteId ou clientes mudam (ex.: filtros da URL)
  useEffect(() => {
    if (!clienteId) {
      setSelectedClienteNome('');
      return;
    }
    const client = (clientes || []).find((c) => String(c.id_cliente) === String(clienteId));
    setSelectedClienteNome(client ? client.nome : '');
  }, [clienteId, clientes]);

  useEffect(() => {
    aplicarFiltrosDaUrl();
  }, [location.search]);

  useEffect(() => {
    if (location.search) return;
    buscarFaturasComEstadoAtual();
  }, [page, search]);

  async function carregarEstados() {
    try {
      const { data } = await api.get('/fatura/estados');
      setEstados(data || []);
    } catch (e) {
      // falha em estados não bloqueia tela
    }
  }

  async function carregarClientes() {
    setLoadingCliente(true);
    try {
      const { data } = await api.get('/cliente?limite=1000');
      setClientes(data.dados || data || []);
    } catch (e) {
      // falha em clientes não bloqueia tela
    }
    setLoadingCliente(false);
  }

  function aplicarFiltrosDaUrl() {
    const searchParams = new URLSearchParams(location.search);

    const paginaUrl = Number(searchParams.get('pagina') || 1);
    const dataVencimentoInicioUrl = searchParams.get('data_vencimento_inicio') || '';
    const dataVencimentoFimUrl = searchParams.get('data_vencimento_fim') || '';
    const criadoInicioUrl = searchParams.get('criado_em_inicio') || '';
    const criadoFimUrl = searchParams.get('criado_em_fim') || '';
    const clientIdUrl = searchParams.get('client_id') || '';

    const estadosParams = searchParams.getAll('id_estado_fatura');
    const estadosFromUrl = [];
    estadosParams.forEach((value) => {
      value.split(',').forEach((part) => {
        if (part) estadosFromUrl.push(part);
      });
    });

    setPage(paginaUrl);
    setClienteId(clientIdUrl);
    setVencimentoInicio(dataVencimentoInicioUrl ? parseISO(dataVencimentoInicioUrl) : null);
    setVencimentoFim(dataVencimentoFimUrl ? parseISO(dataVencimentoFimUrl) : null);
    setEmissaoInicio(criadoInicioUrl ? parseISO(criadoInicioUrl) : null);
    setEmissaoFim(criadoFimUrl ? parseISO(criadoFimUrl) : null);
    setSituacoesSelecionadas(estadosFromUrl);

    buscarFaturas({
      pagina: paginaUrl,
      data_vencimento_inicio: dataVencimentoInicioUrl || undefined,
      data_vencimento_fim: dataVencimentoFimUrl || undefined,
      criado_em_inicio: criadoInicioUrl || undefined,
      criado_em_fim: criadoFimUrl || undefined,
      id_estado_fatura: estadosFromUrl.length ? estadosFromUrl : undefined,
      client_id: clientIdUrl || undefined,
    });
  }

  function getFiltrosAtuais() {
    const searchParams = new URLSearchParams(location.search);
    return {
      pagina: Number(searchParams.get('pagina') || 1),
      data_vencimento_inicio: searchParams.get('data_vencimento_inicio') || undefined,
      data_vencimento_fim: searchParams.get('data_vencimento_fim') || undefined,
      criado_em_inicio: searchParams.get('criado_em_inicio') || undefined,
      criado_em_fim: searchParams.get('criado_em_fim') || undefined,
      id_estado_fatura: searchParams.getAll('id_estado_fatura').filter(Boolean).length
        ? searchParams.getAll('id_estado_fatura')
        : undefined,
      client_id: searchParams.get('client_id') || undefined,
    };
  }

  function buscarFaturasComEstadoAtual() {
    const filtros = getFiltrosAtuais();
    filtros.pagina = page;
    if (search) filtros.nome = search;
    buscarFaturas(filtros);
  }

  async function buscarFaturas(filtros) {
    setLoading(true);
    setError(null);
    toggleLoading(layoutDispatch);

    const params = { pagina: filtros.pagina || 1 };
    if (filtros.data_vencimento_inicio) params.data_vencimento_inicio = filtros.data_vencimento_inicio;
    if (filtros.data_vencimento_fim) params.data_vencimento_fim = filtros.data_vencimento_fim;
    if (filtros.criado_em_inicio) params.criado_em_inicio = filtros.criado_em_inicio;
    if (filtros.criado_em_fim) params.criado_em_fim = filtros.criado_em_fim;
    if (filtros.id_estado_fatura && filtros.id_estado_fatura.length > 0) {
      params.id_estado_fatura = filtros.id_estado_fatura;
    }
    if (filtros.client_id) params.client_id = filtros.client_id;
    if (filtros.nome) params.nome = filtros.nome;

    try {
      const { data } = await api.get('/fatura', { params });
      setTotal(data.totalPaginas || data.total || 1);
      const valorFatura = (item) => {
        let v = item.valor;
        if (v == null && item.Cobranca) v = item.Cobranca.valor;
        if (v == null && item.Cobranca && item.Cobranca.Produto) v = item.Cobranca.Produto.valor;
        return toCurrency(v != null ? v : 0, true);
      };
      setFaturas((data.dados || []).map((item) => ({
        id_fatura: item.id_fatura,
        id_cobranca: item.Cobranca ? item.Cobranca.id_cobranca : null,
        cliente: item.Cobranca && item.Cobranca.Cliente ? item.Cobranca.Cliente.nome : '',
        produto: item.Cobranca && item.Cobranca.Produto ? item.Cobranca.Produto.nome : '',
        valor: valorFatura(item),
        vencimento: basicDate(parseISO(item.vencimento)),
        emissao: basicDate(parseISO(item.criado_em)),
        estado: item.EstadosFatura ? item.EstadosFatura.nome : '',
      })));
    } catch (e) {
      setError(getError(e));
      setFaturas([]);
    }

    setLoading(false);
    toggleLoading(layoutDispatch);
  }

  function atualizarUrlComFiltros(novaPagina = 1) {
    const searchParams = new URLSearchParams();
    searchParams.set('pagina', novaPagina);
    if (vencimentoInicio) searchParams.set('data_vencimento_inicio', dateToBD(vencimentoInicio));
    if (vencimentoFim) searchParams.set('data_vencimento_fim', dateToBD(vencimentoFim));
    if (emissaoInicio) searchParams.set('criado_em_inicio', dateToBD(emissaoInicio));
    if (emissaoFim) searchParams.set('criado_em_fim', dateToBD(emissaoFim));
    if (clienteId) searchParams.set('client_id', clienteId);
    situacoesSelecionadas.forEach((id) => searchParams.append('id_estado_fatura', id));

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }

  function handleAplicarFiltros() {
    atualizarUrlComFiltros(1);
    setFiltersExpanded(false);
  }

  function handleToggleSituacao(idStr) {
    setSituacoesSelecionadas((prev) => {
      const idx = prev.indexOf(idStr);
      if (idx === -1) return [...prev, idStr];
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  }

  function handleLimparCliente() {
    setClienteId('');
    setSelectedClienteNome('');
  }

  function handleLimparSituacao() {
    setSituacoesSelecionadas([]);
  }

  function handleLimparFiltros() {
    setVencimentoInicio(null);
    setVencimentoFim(null);
    setEmissaoInicio(null);
    setEmissaoFim(null);
    setSituacoesSelecionadas([]);
    setClienteId('');
    setSelectedClienteNome('');
    history.push({
      pathname: location.pathname,
      search: '',
    });
  }

  function handleClienteChange(event, newValue) {
    setSelectedClienteNome(newValue || '');
    if (!newValue) {
      setClienteId('');
      return;
    }
    const client = (clientes || []).find((c) => c.nome === newValue);
    setClienteId(client ? String(client.id_cliente) : '');
  }

  function handleChangePage(novaPagina) {
    if (location.search) {
      atualizarUrlComFiltros(novaPagina);
    } else {
      setPage(novaPagina);
    }
  }

  const onSearch = useCallback(
    debounce((query) => {
      setSearch(query);
      if (!location.search) setPage(1);
    }, 500),
    [location.search]
  );

  function handleShowDetalhes(index) {
    const row = faturas[index];
    if (row && row.id_fatura) {
      setDialogFaturaId(row.id_fatura);
      setDialogOpen(true);
    }
  }

  function handleIrCobranca(index) {
    const row = faturas[index];
    if (row && row.id_cobranca) {
      history.push(`/app/cobrancas/${row.id_cobranca}`);
    }
  }

  function handleDialogClose() {
    setDialogOpen(false);
    setDialogFaturaId(null);
    buscarFaturasComEstadoAtual();
  }

  function handleFaturaStatusChange() {
    if (location.search) {
      aplicarFiltrosDaUrl();
    } else {
      buscarFaturas({ ...getFiltrosAtuais(), pagina: page, nome: search });
    }
  }

  const columns = [
    {
      name: 'id_fatura',
      label: '#',
      options: { filter: false, sort: true },
    },
    {
      name: 'cliente',
      label: 'Cliente',
      options: { filter: true, sort: false },
    },
    {
      name: 'produto',
      label: 'Produto',
      options: { filter: true, sort: false },
    },
    {
      name: 'valor',
      label: 'Valor',
      options: { filter: false, sort: false },
    },
    {
      name: 'vencimento',
      label: 'Vencimento',
      options: { filter: true, sort: false },
    },
    {
      name: 'emissao',
      label: 'Emissão',
      options: { filter: true, sort: false },
    },
    {
      name: 'estado',
      label: 'Situação',
      options: { filter: true, sort: false },
    },
    {
      name: 'acoes',
      label: 'Ações',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const row = faturas[tableMeta.rowIndex];
          if (!row) return null;
          return (
            <>
              <Tooltip title="Ver detalhes da fatura">
                <IconButton
                  color="primary"
                  aria-label="detalhes"
                  size="small"
                  onClick={() => handleShowDetalhes(tableMeta.rowIndex)}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              {row.id_cobranca && (
                <Tooltip title="Ir para cobrança">
                  <IconButton
                    color="secondary"
                    aria-label="ir-cobranca"
                    size="small"
                    onClick={() => handleIrCobranca(tableMeta.rowIndex)}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        },
      },
    },
  ];

  return (
    <>
      <PageTitle title="Faturas" />
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget
            title="Filtros"
            disableWidgetMenu
            header={(
              <div
                className={classes.filtersHeader}
                onClick={() => setFiltersExpanded((prev) => !prev)}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') setFiltersExpanded((prev) => !prev); }}
                role="button"
                tabIndex={0}
                aria-expanded={filtersExpanded}
              >
                <Typography variant="h5" color="textSecondary" noWrap>
                  Filtros
                </Typography>
                <IconButton
                  size="small"
                  aria-label={filtersExpanded ? 'Recolher filtros' : 'Expandir filtros'}
                  onClick={(ev) => { ev.stopPropagation(); setFiltersExpanded((prev) => !prev); }}
                >
                  {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </div>
            )}
          >
            <Collapse in={filtersExpanded}>
              <div className={classes.filtersContainer}>
                <Grid container spacing={2} className={classes.filterRow}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Período de vencimento
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KeyboardDatePicker
                      autoOk
                      clearable
                      fullWidth
                      variant="inline"
                      format="dd/MM/yyyy"
                      label="De"
                      value={vencimentoInicio}
                      onChange={setVencimentoInicio}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KeyboardDatePicker
                      autoOk
                      clearable
                      fullWidth
                      variant="inline"
                      format="dd/MM/yyyy"
                      label="Até"
                      value={vencimentoFim}
                      onChange={setVencimentoFim}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom style={{ marginTop: 16 }}>
                      Período de emissão
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KeyboardDatePicker
                      autoOk
                      clearable
                      fullWidth
                      variant="inline"
                      format="dd/MM/yyyy"
                      label="De"
                      value={emissaoInicio}
                      onChange={setEmissaoInicio}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KeyboardDatePicker
                      autoOk
                      clearable
                      fullWidth
                      variant="inline"
                      format="dd/MM/yyyy"
                      label="Até"
                      value={emissaoFim}
                      onChange={setEmissaoFim}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} className={classes.filterRow} alignItems="flex-start">
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start">
                      <Box flex={1}>
                        <Autocomplete
                          value={selectedClienteNome}
                          onChange={handleClienteChange}
                          options={(clientes || []).map((c) => c.nome)}
                          getOptionLabel={(option) => option || ''}
                          disableClearable
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Cliente"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingCliente ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          noOptionsText="Sem opções cadastradas"
                        />
                      </Box>
                      {clienteId ? (
                        <Tooltip title="Remover cliente">
                          <IconButton
                            aria-label="Remover cliente"
                            size="small"
                            onClick={handleLimparCliente}
                            className={classes.clienteClearButton}
                          >
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2} className={classes.filterRow} alignItems="flex-start">
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start">
                      <Box flex={1}>
                        <FormControl fullWidth>
                          <InputLabel id="situacao-label" shrink>Situação</InputLabel>
                          <Button
                            id="situacao-label"
                            fullWidth
                            variant="text"
                            className={classes.situacaoTrigger}
                            onClick={(e) => setSituacaoMenuAnchor(e.currentTarget)}
                            disableRipple
                          >
                            <div className={classes.situacaoTriggerContent}>
                              {situacoesSelecionadas.length === 0 ? (
                                <span className={classes.situacaoPlaceholder}>Nenhuma selecionada</span>
                              ) : (
                                situacoesSelecionadas.map((value) => {
                                  const found = estados.find((e) => String(e.id_estado_fatura) === String(value));
                                  return (
                                    <Chip
                                      key={value}
                                      label={found ? found.nome : value}
                                      size="small"
                                      className={classes.situacaoChip}
                                      style={{ marginRight: 4, marginBottom: 4 }}
                                    />
                                  );
                                })
                              )}
                            </div>
                            <ExpandMoreIcon className={classes.situacaoDropdownIcon} />
                          </Button>
                          <Menu
                            anchorEl={situacaoMenuAnchor}
                            keepMounted
                            open={Boolean(situacaoMenuAnchor)}
                            onClose={() => setSituacaoMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            getContentAnchorEl={null}
                          >
                            {estados.map((estado) => {
                              const idStr = String(estado.id_estado_fatura);
                              const checked = situacoesSelecionadas.indexOf(idStr) !== -1;
                              return (
                                <MenuItem
                                  key={estado.id_estado_fatura}
                                  onClick={() => handleToggleSituacao(idStr)}
                                  dense
                                >
                                  <Checkbox
                                    checked={checked}
                                    tabIndex={-1}
                                    disableRipple
                                    size="small"
                                  />
                                  <ListItemText primary={estado.nome} />
                                </MenuItem>
                              );
                            })}
                          </Menu>
                        </FormControl>
                      </Box>
                      {situacoesSelecionadas.length > 0 ? (
                        <Tooltip title="Remover situação">
                          <IconButton
                            aria-label="Remover situação"
                            size="small"
                            onClick={handleLimparSituacao}
                            className={classes.clienteClearButton}
                          >
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Grid>
                </Grid>

                <div className={classes.filterActions}>
                  <Button
                    variant="outlined"
                    color="default"
                    onClick={handleLimparFiltros}
                    className={classes.actionButton}
                  >
                    Limpar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAplicarFiltros}
                    className={classes.actionButton}
                  >
                    Filtrar
                  </Button>
                </div>
              </div>
            </Collapse>
          </Widget>
        </Grid>

        <Grid item xs={12}>
          {error && (
            <Widget disableWidgetMenu style={{ textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary" style={{ textAlign: 'center' }}>
                Ocorreu um erro ao tentar listar as faturas
              </Typography>
              <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center' }}>
                Mensagem de erro:
                {' '}
                {error}
              </Typography>
              <br />
              <Button
                variant="contained"
                color="primary"
                style={{ marginTop: 16 }}
                onClick={() => (location.search ? aplicarFiltrosDaUrl() : buscarFaturasComEstadoAtual())}
              >
                Tentar novamente
              </Button>
            </Widget>
          )}
          {!error && (
            <TableData
              title="Faturas"
              columns={columns}
              data={faturas}
              search={onSearch}
              page={page}
              total={total}
              onSetPage={handleChangePage}
              onCloseSearch={() => setSearch('')}
              showSearch
              hideOptions
            />
          )}
        </Grid>
      </Grid>

      <FaturaDetalhesDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        faturaId={dialogFaturaId}
        onStatusChange={handleFaturaStatusChange}
      />
    </>
  );
}
